<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.companyNo" placeholder="关联企业编号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.companyName" placeholder="关联企业名称" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.bankAccount" placeholder="来账银行账号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.accountStatus"
          placeholder="状态"
          clearable
        >
          <el-option :label="alipayBankReportStatusLabel[EAlipayBankReportStatus.REPORTING]" :value="EAlipayBankReportStatus.REPORTING" />
          <el-option :label="alipayBankReportStatusLabel[EAlipayBankReportStatus.PASS]" :value="EAlipayBankReportStatus.PASS" />
          <el-option :label="alipayBankReportStatusLabel[EAlipayBankReportStatus.REJECT]" :value="EAlipayBankReportStatus.REJECT" />
        </el-select>
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>
    <div>
      <el-button class="mb-[20px]" type="primary" @click="toAdd">
        <svg-icon icon="lucide:circle-plus" />新增
      </el-button>
      <el-button class="mb-[20px]" type="primary" @click="toBatchAction">
        <svg-icon icon="mingcute:file-export-line" />批量变更状态
      </el-button>
      <el-button class="mb-[20px]" type="primary" @click="exportFile">
        <svg-icon icon="lucide:square-arrow-out-up-right" />导出
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table ref="tableRef" v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" :cell-style="initOperatorColumnStyle">
      <el-table-column align="center" type="selection" :min-width="55" />
      <el-table-column align="center" type="index" label="序号" :min-width="80" />
      <el-table-column align="center" prop="companyNo" label="关联企业编号" :min-width="160" />
      <el-table-column align="center" prop="companyName" label="关联企业名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="center" prop="rechargeCompanyName" label="来账企业名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="center" prop="bankName" label="来账银行名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="center" prop="bankAccount" label="来账银行账号" :min-width="220" />
      <el-table-column align="center" prop="accountStatusText" label="状态" :min-width="140">
        <template #default="scope">
          <el-tag :type="alipayBankReportStatusStyle[scope.row.accountStatus]">
            {{ scope.row.accountStatusText }}
          </el-tag>
          <el-tooltip v-if="scope.row.accountStatus === EAlipayBankReportStatus.REJECT" :content="scope.row.rejectReason" placement="top">
            <svg-icon class="!w-[16px] !h-[16px] ml-1 main-primary-text " style="vertical-align: -0.25rem;" icon="lucide:info" />
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column align="center" prop="createTime" label="创建时间" :min-width="220" />
      <el-table-column
        v-if="buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        :width="180"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" @close="showAddAcount = false" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>

    <add-acount ref="addAcountRef" :visible="showAddAcount" type="add" :server-id="state.businessId" @close="showAddAcount = false" @confirm="confirmAddAcount" />
    <batch-action ref="batchActionRef" :visible="showBatchAction" :selected-arr-ids="selectedArrIds" @close="showBatchAction = false" @confirm="confirmBatchAction" />
  </content-container>
</template>

<script setup lang="ts">
import AddAcount from './dialogs/AddAcount.vue';
import BatchAction from './dialogs/BatchAction.vue';
import { reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { initOperatorColumnStyle, onConfirm, SvgIcon, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  queryAlipayUnitBankAccountPage,
  getCustomState,
  deleteUnitBankAccount,
  exportAlipayUnitBankAccount,
} from '@core/main';
import { alipayBankReportStatusLabel, alipayBankReportStatusStyle, EAlipayBankReportStatus } from '@core/main/types';
import { downloadFile } from '@core/tools';

defineOptions({ name: 'AlipayIncomingAccount' });

const defaultPageSize = 12;

const state = getCustomState<any>();

const params = reactive<any>({
  page: 1,
  size: defaultPageSize,
});

const loading = ref(false);
const total = ref(0);

const showAddAcount = ref(false);
const showBatchAction = ref(false);
const currentRow = ref<any>({});
const tableData = ref<any[]>([]);
const tableRef = ref<any>(null);
const selectedArrIds = ref<any[]>([]);
const { buttons } = useTblPermissionButtons([
  {
    name: '更改状态',
    action: tochangeAction,
  },

  {
    name: '删除',
    relationField: 'accountStatus',
    relationValue: EAlipayBankReportStatus.REJECT,
    action: deleteAction,
  },
]);

function toAdd() {
  showAddAcount.value = true;
}

function toBatchAction() {
  selectedArrIds.value = tableRef.value.getSelectionRows().map((v) => v.id);

  if (selectedArrIds.value.length === 0) return ElMessage.warning('请先选择需要变更状态的记录');

  showBatchAction.value = true;
}

function tochangeAction(row) {
  selectedArrIds.value = [row.id];
  showBatchAction.value = true;
}

async function exportFile() {
  try {
    const { buffer, fileName } = await exportAlipayUnitBankAccount({
      ...params,
      serverId: state.businessId,
    });

    downloadFile(buffer, fileName, 'xlsx');
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function deleteAction(row) {
  currentRow.value = row;

  onConfirm({
    message: '确认要删除吗？',
    onOk: async () => {
      try {
        await deleteUnitBankAccount({ id: row.id });
        ElMessage.success('删除成功');
        doSearch();
      } catch (error) {
        if ((error as Error).message) ElMessage.error((error as Error).message);
      }
    },
  });
}

async function fetchData() {
  loading.value = true;

  try {
    const res = await queryAlipayUnitBankAccountPage({
      ...params,
      serverId: state.businessId,
    });

    tableData.value = res.data;
    total.value = res.total;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function confirmAddAcount() {
  showAddAcount.value = false;
  fetchData();
}

function confirmBatchAction() {
  showBatchAction.value = false;
  fetchData();
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

fetchData();
</script>
